import { useState, useEffect, useCallback } from 'react';
import {
  fetchProviderTemplates,
  fetchProviders,
  createProvider as apiCreateProvider,
  updateProvider as apiUpdateProvider,
  deleteProvider as apiDeleteProvider,
  createCustomModel as apiCreateCustomModel,
  updateCustomModel as apiUpdateCustomModel,
  deleteCustomModel as apiDeleteCustomModel,
} from './provider-api';
import type {
  Provider,
  ProviderTemplate,
  CreateProviderRequest,
  UpdateProviderRequest,
  CustomModelFormData,
} from './provider-types';

/** Fetch available provider templates (OpenAI, Anthropic, etc.). */
export function useProviderTemplates() {
  const [templates, setTemplates] = useState<ProviderTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchProviderTemplates();
        if (!cancelled) setTemplates(data);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load templates');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return { templates, loading, error };
}

/** Workspace-scoped configured providers with CRUD operations. */
export function useProviders(workspaceId: string | null) {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchProviders(workspaceId);
      setProviders(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load providers');
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  const create = useCallback(async (body: CreateProviderRequest) => {
    if (!workspaceId) throw new Error('No workspace');
    const provider = await apiCreateProvider(workspaceId, body);
    await load();
    return provider;
  }, [workspaceId, load]);

  const update = useCallback(async (providerId: number, body: UpdateProviderRequest) => {
    if (!workspaceId) throw new Error('No workspace');
    const provider = await apiUpdateProvider(workspaceId, providerId, body);
    await load();
    return provider;
  }, [workspaceId, load]);

  const remove = useCallback(async (providerId: number) => {
    if (!workspaceId) throw new Error('No workspace');
    await apiDeleteProvider(workspaceId, providerId);
    await load();
  }, [workspaceId, load]);

  const addCustomModel = useCallback(async (providerId: number, body: CustomModelFormData) => {
    if (!workspaceId) throw new Error('No workspace');
    const model = await apiCreateCustomModel(workspaceId, providerId, body);
    await load();
    return model;
  }, [workspaceId, load]);

  const editCustomModel = useCallback(async (providerId: number, modelId: number, body: Partial<CustomModelFormData>) => {
    if (!workspaceId) throw new Error('No workspace');
    const model = await apiUpdateCustomModel(workspaceId, providerId, modelId, body);
    await load();
    return model;
  }, [workspaceId, load]);

  const removeCustomModel = useCallback(async (providerId: number, modelId: number) => {
    if (!workspaceId) throw new Error('No workspace');
    await apiDeleteCustomModel(workspaceId, providerId, modelId);
    await load();
  }, [workspaceId, load]);

  return {
    providers, loading, error, refresh: load,
    create, update, remove,
    addCustomModel, editCustomModel, removeCustomModel,
  };
}
